import cn from 'classnames';
import { isEmpty } from 'lodash-es';
import { FC, memo } from 'react';
import { useIntl } from 'react-intl';

import { Decision } from '@/shared/api/protocol-ts/model/dto_report_common_pb';
import { Checkbox, Icon, Radio } from '@/shared/ui';

import { conditionText } from '../../config';
import {
  CompactCondition,
  ConditionColor,
  ConditionDecisionChange,
} from '../../config/types';

import styles from './ConditionToggle.module.scss';

const colorStyle: Record<ConditionColor, string> = {
  yellow: styles.colorYellow,
  red: styles.colorRed,
  purple: styles.colorPurple,
  white: styles.colorWhite,
};

type ConditionToggleProps = {
  className?: string;
  data: CompactCondition;
  disabled?: boolean;
  onChange: ConditionDecisionChange;
};

type ConditionToggleProbabilityProps = {
  probability: CompactCondition['probability'];
  probabilityText: CompactCondition['probabilityText'];
  modelPositive: CompactCondition['modelPositive'];
};

const ConditionToggleProbability = (props: ConditionToggleProbabilityProps) => {
  const { probability, probabilityText, modelPositive } = props;

  if (modelPositive) {
    return (
      <div className={styles.probabilityCat}>
        <Icon name="cat" size={16} />
        <span className={styles.probabilitCatText}>{probabilityText}</span>
      </div>
    );
  }

  if (typeof probability === 'number') {
    return <span className={styles.probability}>{probabilityText}</span>;
  }

  return null;
};

const InnerConditionToggle: FC<ConditionToggleProps> = (props) => {
  const { className, data, disabled, onChange } = props;

  const { formatMessage } = useIntl();

  if (isEmpty(data)) {
    return null;
  }

  const name = data.isOneOf ? data.group : data.code.toString();
  const Component = data.isOneOf ? Radio : Checkbox;

  const handleChange = () => {
    onChange({
      conditionIDs: data.ids,
      parentConditionId: data.parentID,
      parentConditionCode: data.parentCode,
      conditionCode: data.code,
      userDecision: data.isChecked
        ? Decision.NegativeDecision
        : Decision.PositiveDecision,
    });
  };

  return (
    <Component
      id={data?.id}
      className={cn(
        styles.container,
        { [styles.disabled]: disabled },
        className,
      )}
      checked={data.isChecked ?? false}
      disabled={disabled}
      onChange={handleChange}
      name={name}
    >
      <div className={styles.label}>
        <span className={cn(styles.text, colorStyle[data.color])}>
          {data.code in conditionText &&
            formatMessage(conditionText[data.code])}
        </span>
        <ConditionToggleProbability
          probability={data.probability}
          probabilityText={data.probabilityText}
          modelPositive={data.modelPositive}
        />
      </div>
    </Component>
  );
};

export const ConditionToggle = memo(InnerConditionToggle);
